import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import PageTitle from "../../components/PageTitle";
import { Calender, TextInputForm, DropDown } from "../../components/Forms";
import { Buttons } from "../../components/Buttons";
import Footer from "../../components/Footer";
import Dialog from "../../components/Dialog";
import {
  addBalanceSheet,
  updateBalanceSheet,
  fetchGroupedBalanceSheet,
} from "../../slices/balanceSheetSlice";
import { fetchAgent } from "../../slices/agentSlice";
import NotifyData from "../../components/NotifyData";

const BalanceSheetCreations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { agent } = useSelector((state) => state.agent);
  const { totals } = useSelector((state) => state.balanceSheet);

  const { isEdit = false, data = null } = location.state || {};

  const [balanceSheetDetails, setBalanceSheetDetails] = useState({
    balance_sheet_id: "",
    entry_date: new Date().toISOString().split("T")[0],
    agent_name: "",
    agent_id: "",
    amount: "",
    description: "",
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isAgentPrefilled, setIsAgentPrefilled] = useState(false);

  useEffect(() => {
    dispatch(fetchAgent());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setBalanceSheetDetails({
        balance_sheet_id: data.balance_sheet_id || "",
        entry_date: data.entry_date || new Date().toISOString().split("T")[0],
        agent_name: data.agent_name || "",
        agent_id: data.agent_id || "",
        amount: data.amount || "",
        description: data.description || "",
      });
      if (data.agent_name) {
        setIsAgentPrefilled(true);
      }
    }
  }, [data]);

  useEffect(() => {
    if (balanceSheetDetails.agent_name) {
      // Fetch balance for the selected agent (all time)
      dispatch(
        fetchGroupedBalanceSheet({
          fromDate: "",
          toDate: "",
          nameFilter: balanceSheetDetails.agent_name,
        })
      );
    }
  }, [dispatch, balanceSheetDetails.agent_name]);

  const handleInputChange = (field, value) => {
    setBalanceSheetDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAgentChange = (e) => {
    if (isAgentPrefilled) return;
    const selectedName = e.target.value;
    const selectedAgent = agent.find((ag) => ag.agent_name === selectedName);
    setBalanceSheetDetails((prev) => ({
      ...prev,
      agent_name: selectedName,
      agent_id: selectedAgent ? selectedAgent.agent_id : null,
    }));
  };

  const handleSave = async () => {
    if (
      !balanceSheetDetails.entry_date ||
      !balanceSheetDetails.agent_name ||
      !balanceSheetDetails.amount ||
      !balanceSheetDetails.description
    ) {
      NotifyData(
        "Entry date, agent, amount, and description are required!",
        "error"
      );
      return;
    }

    try {
      let result;
      if (isEdit && balanceSheetDetails.balance_sheet_id) {
        result = await dispatch(
          updateBalanceSheet(balanceSheetDetails)
        ).unwrap();
        NotifyData("Balance sheet entry updated successfully!", "success");
      } else {
        result = await dispatch(addBalanceSheet(balanceSheetDetails)).unwrap();
        NotifyData("Balance sheet entry added successfully!", "success");
      }
      // Navigate to agent-specific balance sheet if agent_name is set
      if (balanceSheetDetails.agent_name) {
        navigate(
          `/agent-balance-sheet/${encodeURIComponent(
            balanceSheetDetails.agent_name
          )}`
        );
      } else {
        navigate("/invoices");
      }
    } catch (error) {
      NotifyData(
        `Failed to ${
          isEdit ? "update" : "add"
        } balance sheet entry. Please try again!`,
        "error"
      );
      console.error(error);
    }
  };

  const handleCloseForm = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClose = (confirm) => {
    setShowConfirmDialog(false);
    if (confirm) {
      if (balanceSheetDetails.agent_name) {
        navigate(
          `/agent-balance-sheet/${encodeURIComponent(
            balanceSheetDetails.agent_name
          )}`
        );
      } else {
        navigate("/invoices");
      }
    }
  };

  const pageTitle = isEdit
    ? "Edit Balance Sheet Entry"
    : "Create Balance Sheet Entry";

  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col lg="12">
            <PageTitle
              PageTitle={pageTitle}
              showButton={true}
              OnClick={handleCloseForm}
            />
          </Col>
          <Col lg="12" className="py-3">
            <div className="content-box">
              <Row>
                <Col lg="4" md="6" xs="12" className="py-3">
                  <Calender
                    textlabel="Entry Date"
                    value={balanceSheetDetails.entry_date}
                    onChange={(date) => handleInputChange("entry_date", date)}
                  />
                </Col>
                <Col lg="4" md="6" xs="12" className="py-3">
                  <DropDown
                    textlabel="Agent"
                    options={[
                      { label: "Select Agent", value: "" },
                      ...agent.map((a) => ({
                        label: a.agent_name,
                        value: a.agent_name,
                      })),
                    ]}
                    value={balanceSheetDetails.agent_name}
                    onChange={handleAgentChange}
                    disabled={isAgentPrefilled}
                  />
                </Col>
                <Col lg="4" md="6" xs="12" className="py-3">
                  <TextInputForm
                    textlabel="Amount"
                    value={balanceSheetDetails.amount}
                    onChange={(e) =>
                      handleInputChange("amount", e.target.value)
                    }
                    type="number"
                    placeholder="Enter amount"
                  />
                </Col>
                <Col lg="4" md="6" xs="12" className="py-3">
                  <TextInputForm
                    textlabel="Description"
                    value={balanceSheetDetails.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Enter description"
                  />
                </Col>
              </Row>
            </div>
          </Col>
          {balanceSheetDetails.agent_name && (
            <Col lg="6" className="py-3">
              <div className="content-box">
                <Row>
                  <Col lg="12">
                    <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                      <div>
                        <h5 className="mb-1">
                          Remaining to Pay for {balanceSheetDetails.agent_name}
                        </h5>
                        <p className={`fw-bold mb-0 text-danger`}>
                          Rs. {totals.balance.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
          <Col lg="12">
            <Footer
              LabelOne={<>Save</>}
              LabelTwo={<>Cancel</>}
              LabelOneClick={handleSave}
              LabelTwoClick={handleCloseForm}
            />
          </Col>
        </Row>
      </Container>
      <Dialog
        DialogTitle="Do You Want Close ?"
        isVisible={showConfirmDialog}
        onConfirm={handleConfirmClose}
        onCancel={() => handleConfirmClose(false)}
      />
    </div>
  );
};

export default BalanceSheetCreations;
